import React, { useState, useEffect, useContext, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useMutation, ClientContext } from "graphql-hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";
import {
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
  Button,
  FormHelperText,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import logoImg from "./assets/logo.png";
import loginImg from "./assets/login-image.png";
import mascotJoin from "./assets/login-mascot.png";
import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import { REFRESH_TOKEN_MUTATION } from "./graphQL/user_qry";
const admin_string = process.env.REACT_APP_ADMIN_URL || "/t31u4YYky50K";
const domain_name = process.env.REACT_APP_DOMAIN_URL || "https://r3play.com";



const StyledContainer = styled(Container)(() => ({            
    minHeight: 624,
    angle: "0 deg",
    opacity: 1,
    borderRadius: "16px",
    boxShadow: "0px 4px 12px 0px #0000001F",
    display: "flex",
    justifyContent: "center",
    justifyItems: "center" ,
    // DESKTOP
    maxWidth: "1080px",
    margin: "48px 150px",

    "@media (max-width: 1360px)": {
        maxWidth: "802px",      // TABLET WIDTH
        margin: "32px auto",
    },
    "@media (max-width: 856px)": {
        maxWidth: "361px",      // MOBILE WIDTH
        margin: "8px auto",
    },    
}));

const FormSide = styled(Box)(() => ({
  
   width: "540px",             // Desktop 2 columns = 1080 total
   padding: "32px",
   gap: "16px",
   minHeight: 624,
  "@media (max-width: 1360px)": 
  { width: "401px" },// Tablet 2 columns = 802 total
  
  "@media (max-width: 856px)":  
  {  width: "100%",            // Mobile = single column
    padding: "16px",          // Reduce padding on mobile 
    height: "auto",
    minHeight: "auto"
  },  
  angle: "0 deg",
  opacity: 1,
}));

const StyledTextField = styled(TextField)(() => ({
    marginBottom: 5,
    marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
}));

const StyledFormHelperTextError = styled(FormHelperText)(() => ({
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
}));

const StyledBox  = styled(Box)(() => ({
    color: "#212121",
    backgroundColor: "#F0FEF4",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
}));

const StyledButton = styled(Button)(() => ({
    width: "158px",
    height: "48px",
    backgroundColor: "#EF5658",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#EF5658",
    },
    "&:hover": {
      backgroundColor: "#D33338",
    },
}));

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const LOGIN_USER_MUTATION = `
    mutation loginUser($email: String!, $password: String!) {
      tokenAuth(email: $email, password: $password) {
        token
        refreshToken
        payload
      }
    }
`;

const LOGIN_AUTO_MUTATION = `
    mutation loginAuto($username: String!) {
      loginAuto(username: $username) {
      userId
      token
      refreshToken
      role
      }
    }
`;

const SignIn = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const form = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [autoLoginFlag, setAutoLoginFlag] = useState(localStorage.getItem("autoLogin"));
  const [usernameFlag, setUsernameFlag] = useState(localStorage.getItem("userName"));
  const [loginUser] = useMutation(LOGIN_USER_MUTATION);
  const [loginAuto] = useMutation(LOGIN_AUTO_MUTATION);
  const navigate = useNavigate();
  const client = useContext(ClientContext);
  const [refreshToken] = useMutation(REFRESH_TOKEN_MUTATION, {
    onError: err => {
      console.log(err);
    },
  });
  const [nextUrl] = useSearchParams();
  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (
      localStorage.getItem("autoLogin") &&
      localStorage.getItem("userName") &&
      localStorage.getItem("userUuid") &&
      localStorage.getItem("autoLogin") === "true"
    ) {
      client.removeHeader("Authorization");
      console.log("onAutoLogin");
      onAutoLogin();
    } else if (localStorage.getItem("refreshToken")) {
      console.log("onRefreshToken");
      onRefreshToken();
    }
  }, [localStorage]);

  const onRefreshToken = async () => {
    const { data: refreshData, error: refreshError } = await refreshToken({
      variables: { refreshToken: localStorage.getItem("refreshToken") },
    });
    if (refreshData && refreshData.refreshToken) {
      localStorage.setItem("token", refreshData.refreshToken.token);
      localStorage.setItem("refreshToken", refreshData.refreshToken.refreshToken);
      client.setHeader("Authorization", `JWT ${refreshData.refreshToken.token}`);
      if (refreshData.refreshToken.payload.role === "Regular") {
        navigate(`/dashboard`);
      } else if (refreshData.refreshToken.payload.role === ""){
        alert("Wait a minute! You are in the wrong place. Start from " + domain_name);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("userUuid");
        client.removeHeader("Authorization");
        window.location.replace(domain_name);
      }
    }
  };

  const onAutoLogin = async () => {
    const { data: refreshData, error: refreshError } = await loginAuto({
      variables: { username: localStorage.getItem("userName") },
    });
    console.log("data:", refreshData, "error:", refreshError);
    if (refreshError) {
      if (
        refreshError.graphQLErrors[0].message ===
        "You do not have permission to perform this action"
      )
        navigate("/signin");
      if (refreshError.graphQLErrors[0].message === "Signature has expired")
        navigate("/signin");
    }
    if (refreshData && refreshData.loginAuto) {
      localStorage.setItem("token", refreshData.loginAuto.token);
      localStorage.setItem("refreshToken", refreshData.loginAuto.refreshToken);
      client.setHeader("Authorization", `JWT ${refreshData.loginAuto.token}`);
      if (refreshData.refreshToken.payload.role === "Regular") {
        navigate(`/dashboard`);
      } else if (refreshData.refreshToken.payload.role === ""){
        alert("Wait a minute! You are in the wrong place. Start from " + domain_name);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("role");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("userUuid");
        client.removeHeader("Authorization");
        window.location.replace(domain_name);
      }
    }
  };

  /*
   * Function : setTimeout
   * Description : Disappear message after timeout
   */
  setTimeout(() => {
    setSuccessMsg("");
    setErrorMsg("");
  }, 10000000000);

  const onSubmit = async formData => {
    await sleep(2000);
    console.log("DATA-----------", formData);
    const { data, error } = await loginUser({
      variables: {
        email: formData.email,
        password: formData.password,
      },
    });
    if (error) {
      console.log("Error", error, data);
      setErrorMsg(
        "Incorrect email or password. Or, your account is not activated. Or, this email does not exist. Please try again."
      );
      setTimeout("");
    } else {
      if (data.tokenAuth.payload.role === "Regular") {
        localStorage.setItem("role", data.tokenAuth.payload.role);
        localStorage.setItem("token", data.tokenAuth.token);
        localStorage.setItem("refreshToken", data.tokenAuth.refreshToken);
        localStorage.setItem("email", data.tokenAuth.payload.email);
        localStorage.setItem("userName", data.tokenAuth.payload.username);
        localStorage.setItem("firstName", data.tokenAuth.payload.firstName);
        localStorage.setItem("userUuid", data.tokenAuth.payload.userUuid);
        localStorage.setItem("autoLogin", data.tokenAuth.payload.autoLogin);
        client.setHeader("Authorization", `JWT ${data.tokenAuth.token}`);
        if (nextUrl && nextUrl.get("next")) {
          navigate(`${nextUrl.get("next")}`);
        } else {
          navigate(`/dashboard`);
        }
      } else {
        alert("Wait a minute! You are in the wrong place. Start from " + domain_name);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("email");
        client.removeHeader("Authorization");
        window.location.replace(domain_name);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);    
  }, []);

  return (
    <Grid>
      <>
        <Helmet>
          <title>R3PLAY - Sign In</title>
          <meta
            name="description"
            content="Access your R3PLAY account to manage your toys, purchases, and trades"
          />
          <meta
            property="keywords"
            content="Sign in, toy marketplace login"
          />        
        </Helmet>
        <form ref={form} onSubmit={handleSubmit(onSubmit)}>            
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
           {!isMobile ?
            (
            <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns= "1fr 1fr"                 
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children
                  alignItems="start"
                  gap="0"
                  sx={{ width: "100%" }}
                >
                <FormSide sx={{ borderRight: "1px solid #e0e0e0" }}>
                   <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant={!isTablet ? "h2" : "h3"}
                        fontSize={!isTablet ? "36px" : "30px"}
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Sign in
                      </Typography>
                  </Box>

                    <Box flexGrow={1} mb={1}>
                      <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {errors.password && errors.password.type === "required" && (
                        <StyledFormHelperTextError>
                          Password cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError sx={{color: "#E53935"}}>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex" justifyContent="left">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SIGN IN
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={4} 
                         sx={{
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                          textAlign: "left", // center text on mobile
                        }}>
                      
                       { isTablet ? (
                      <>
                        
                        <Typography component="div">Don't have an account yet?</Typography>

                       
                        <Box sx={{ display: "flex", justifyContent: "left", gap: 1, mt: 1 }}>
                          <Link component={RouterLink} to="/signup" color="primary" underline="none">
                            Sign Up
                          </Link>
                          <span>|</span>
                          <Link component={RouterLink} to="/reset-password" color="primary" underline="none">
                            Reset Password
                          </Link>
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* Desktop */}
                        Don't have an account yet?
                        <Link ml={1} mr={1} component={RouterLink} to="/signup" color="primary" underline="none">
                          Sign Up
                        </Link>
                        |
                        <Link ml={1} component={RouterLink} to="/reset-password" color="primary" underline="none">
                          Reset Password
                        </Link>
                      </>
                    )}
                    </Box>
                  </FormSide>

                  <FormSide sx={{paddingLeft: "0 !important"}}>
                    <Box flexGrow={1}>                      
                          <Box sx={{ paddingLeft: "32px" }}>
                                  <img src={logoImg} alt="logo" width={"40%"} />
                          </Box>
                      
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        paddingLeft: "32px"
                        }}>
                        <br />
                        A big hi from R3PLAY! So delighted to see you back. Enjoy your session.
                        <br /><br />
                        R3PLAY platform is a place where toys, stories, and nostalgic joy come to life. It is a social commerce space where you can buy, sell, trade, donate, and share your love of toys with other collectors, kidults, parents, and enthusiasts just like you.
                      </Typography>
                      <Box>
                        <img src={loginImg} alt="logo" 
                               style={{
                                  width: isTablet ? "420px" : "540px",
                                 }} />
                      </Box>
                    </Box>                    
                  </FormSide>
                </Box>
              </StyledContainer>
              )
              :
              (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr"                  
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children                  
                  gap="0"                
                  sx={{ width: "100%" }}
                >

                <FormSide >

                <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant="h3"
                        fontSize="30px"
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Sign in
                      </Typography>
                  </Box>
                  
                    <Box flexGrow={1}>                     
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        
                        }}>
                        <br />
                        A big hi from R3PLAY! So delighted to see you back. Enjoy your session.
                        <br /><br />
                        R3PLAY platform is a place where toys, stories, and nostalgic joy come to life. It is a social commerce space where you can buy, sell, trade, donate, and share your love of toys with other collectors, kidults, parents, and enthusiasts just like you.
                      </Typography>
                      <Box>
                        <img src={loginImg} alt="logo" width={"100%"} />
                      </Box>
                   </Box>                    
                  </FormSide>

                <FormSide sx={{ borderRight: "none" }}>
                  

                    <Box flexGrow={1} mb={1}>
                      <StyledTextField
                        fullWidth
                        //autoFocus
                        label="Email" // Address"
                        margin="normal"
                        name="email"
                        type="email"
                        variant="outlined"
                        id="email"
                        {...register("email", {
                          required: true,
                          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        })}
                      />
                      {errors.email && errors.email.type === "required" && (
                        <StyledFormHelperTextError>
                          Email address cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errors.email && errors.email.type === "pattern" && (
                        <StyledFormHelperTextError>
                          Enter a correctly-formed email like myname@xyz.com
                        </StyledFormHelperTextError>
                      )}
                      <StyledTextField
                        fullWidth
                        label="Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {errors.password && errors.password.type === "required" && (
                        <StyledFormHelperTextError>
                          Password cannot be blank.
                        </StyledFormHelperTextError>
                      )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError sx={{color: "#E53935"}}>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex" justifyContent="left">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SIGN IN
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box mt={4} 
                         sx={{
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                          fontWeight: "400",
                          lineHeight: "1.7",
                          letterSpacing: "0.00938em",
                          textAlign: "left", // center text on mobile
                        }}>
                      
                                               
                        <Typography component="div">Don't have an account yet?</Typography>

                       
                        <Box sx={{ display: "flex", justifyContent: "left", gap: 1, mt: 1 }}>
                          <Link component={RouterLink} to="/signup" color="primary" underline="none">
                            Sign Up
                          </Link>
                          <span>|</span>
                          <Link component={RouterLink} to="/reset-password" color="primary" underline="none">
                            Reset Password
                          </Link>
                        </Box>                      
                    </Box>
                  </FormSide>
                  
                </Box>
              </StyledContainer>
              )}
                {!isTablet && !isMobile
                  ?
                  (
                    <Box
                      sx={{
                        position: "relative",
                        bottom: "-130px",
                        right: 145,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={mascotJoin}
                        alt="Join the Fun Mascot Orange"
                        sx={{
                          width: "140px",
                          height: "auto",
                          
                        }}
                      />
                    </Box>
                  )
                  :""
                }
          </Box>
          </form>
        </>
    </Grid>
  );
};

export default SignIn;
